/**
 * Analytics Dashboard - Main Page
 * Provides comprehensive analytics and reporting capabilities
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { Download, TrendingUp, Users, MessageSquare, Calendar, CheckSquare, FileText, Brain, DollarSign, Zap, Activity, AlertCircle, BarChart3, Copy, Trash2, Edit3, FolderOpen, Save, Send, Wifi, ChevronDown, ChevronUp, Loader2, Printer, FileDown, ClipboardList } from 'lucide-react';
import { CommunicationHistoryPage } from '../communications/CommunicationHistoryPage';
import { BarChartComponent, PieChartComponent } from './ChartComponents';
import { ReportBuilderWizard } from './ReportBuilderWizard';
import CapabilityGate from '../common/CapabilityGate';
import EnrichmentDashboard from '../enrichment/EnrichmentDashboard';
import ConsentDashboard from '../consent/ConsentDashboard';
import ProgramMilestoneDashboardSelector from '../incentives/ProgramMilestoneDashboardSelector';
import { CapacityDashboard } from '../CapacityDashboard';
import TelehealthReporting from '../telehealth/TelehealthReporting';
import ReportDistributionPage from '@/src/app/admin/report-distribution/page';
import { UUHPCampaignProgressPage } from '@/components/communications/EngagementDemoFlowComponents';
import { EngagementInsightsPanel } from '@/components/communications/insights/EngagementInsightsPanel';
import { EnvironmentalImpactCard } from '@/components/communications/insights/EnvironmentalImpactCard';
import { ECMInsightsPanel } from '@/components/ecm-insights/ECMInsightsPanel';
import EcmProgressNotesReport from '@/components/reports/EcmProgressNotesReport';
import SfhpInvoiceReport from '@/components/reports/SfhpInvoiceReport';
import { MemberRetentionDashboard } from '@/components/retention';
import ExportableCard from './ExportableCard';
import ReportLibraryPanel, { DEFAULT_REPORT_CATALOG } from './ReportLibraryPanel';
import ScheduledReportsPage from '@/src/app/admin/scheduled-reports/page';
import EnrollmentReportPanel from '../reports/EnrollmentReportPanel';
import SurveyResultsReport from '../reports/SurveyResultsReport';
import DcmCaseOverviewPanel from '../reports/DcmCaseOverviewPanel';
import DcmTeamActivityPanel from '../reports/DcmTeamActivityPanel';
import { useCapability } from '@/hooks/useCapabilities';
import { usePageCapabilities } from '@/hooks/usePageCapability';
import { filterReportCatalog, isReportHidden, reportLibraryPageKeys } from './reportLibraryAccess';
import { getActiveCboId } from '@/services/api/organizations';

import { useAppTranslation } from '@/contexts/TranslationContext';
import { config as productConfig } from '@/src/config';
// Analytics API is on the WeaveIn backend
const API_URL = productConfig.weaveinApi.baseUrl ?? 'http://localhost:8000';

// Build axios headers with both Authorization (JWT) and X-CBO-ID (active org).
// Without X-CBO-ID, super_admins/multi-org users can see another org's data.
const _buildAxiosAuthHeaders = (token: string | null): Record<string, string> => {
  const h: Record<string, string> = {};
  if (token) h.Authorization = `Bearer ${token}`;
  const cboId = localStorage.getItem('active_cbo_id');
  if (cboId) h['X-CBO-ID'] = cboId;
  return h;
};

type AnalyticsTab = 'custom' | 'executive' | 'referrals' | 'members' | 'communications' | 'programs' | 'tasks' | 'enrichment' | 'consent' | 'incentives' | 'provider-performance' | 'telehealth' | 'campaign-progress' | 'report-distribution' | 'scheduled-reports' | 'engagement-insights' | 'ecm-insights' | 'ecm-progress-notes' | 'sfhp-invoice' | 'member-retention' | 'environmental-impact' | 'enrollments' | 'dcm-overview' | 'dcm-activity' | 'survey-results';

interface AnalyticsDashboardProps {
  initialTab?: AnalyticsTab;
}

const REPORT_LIBRARY_PAGE_KEYS = reportLibraryPageKeys(DEFAULT_REPORT_CATALOG);

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ initialTab = 'custom' }) => {
  const { t } = useAppTranslation();
  // Get organization ID from localStorage
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  const [loading, setLoading] = useState(false);
  // Hash-aware initial tab so report URLs are bookmarkable (e.g. #report=members)
  const _readHashReport = (): AnalyticsTab | null => {
    if (typeof window === 'undefined') return null;
    const m = window.location.hash.match(/(?:^#|&)report=([a-z-]+)/i);
    if (!m) return null;
    const v = m[1] as AnalyticsTab;
    return v;
  };
  const [activeTab, setActiveTab] = useState<AnalyticsTab>(_readHashReport() || initialTab);
  const mainScrollRef = useRef<HTMLDivElement>(null);
  const [pageExportBusy, setPageExportBusy] = useState<null | 'pdf' | 'print'>(null);
  const [savedReports, setSavedReports] = useState<any[]>([]);
  const [savedReportsLoading, setSavedReportsLoading] = useState(false);
  const [schemaSources, setSchemaSources] = useState<any[]>([]);
  const [reportTemplates, setReportTemplates] = useState<any[]>([]);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date()
  });

  // Analytics Data States
  const [referralData, setReferralData] = useState<any>(null);
  const [memberData, setMemberData] = useState<any>(null);
  const [communicationData, setCommunicationData] = useState<any>(null);
  const [programData, setProgramData] = useState<any>(null);
  const [taskData, setTaskData] = useState<any>(null);
  const [executiveData, setExecutiveData] = useState<any>(null);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportResult, setReportResult] = useState<any>(null);
  const [runningReportId, setRunningReportId] = useState<number | null>(null);
  const [aiDashboardData, setAiDashboardData] = useState<any>(null);
  const [referralOutcomes, setReferralOutcomes] = useState<any>(null);
  const [commEngagement, setCommEngagement] = useState<any>(null);
  const [memberEnrichment, setMemberEnrichment] = useState<any>(null);
  const [insights, setInsights] = useState<any>(null);

  // Distribution setup for saved reports
  const [distributeReportId, setDistributeReportId] = useState<number | null>(null);
  const [distAffiliations, setDistAffiliations] = useState<any[]>([]);
  const [distAffiliationId, setDistAffiliationId] = useState('');
  const [distDelivery, setDistDelivery] = useState('download');
  const [distSftpHost, setDistSftpHost] = useState('');
  const [distSftpPort, setDistSftpPort] = useState('22');
  const [distSftpUser, setDistSftpUser] = useState('');
  const [distSftpDir, setDistSftpDir] = useState('');
  const [distRequiresApproval, setDistRequiresApproval] = useState(false);
  const [distSaving, setDistSaving] = useState(false);

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
      const _cboId = localStorage.getItem('active_cbo_id');
      if (_cboId) headers['X-CBO-ID'] = _cboId;
      const params = {
        start_date: dateRange.startDate.toISOString(),
        end_date: dateRange.endDate.toISOString()
      };

      // Fetch data from all endpoints in parallel (each with individual error handling)
      const [referrals, members, communications, programs, tasks, executive, aiDashboard] = await Promise.all([
        axios.get(`${API_URL}/api/analytics/referrals/summary`, { headers, params }).catch((e) => { console.error('Referrals fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/analytics/members/summary`, { headers, params }).catch((e) => { console.error('Members fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/analytics/communications/summary`, { headers, params }).catch((e) => { console.error('Communications fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/analytics/programs/summary`, { headers }).catch((e) => { console.error('Programs fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/analytics/tasks/summary`, { headers, params }).catch((e) => { console.error('Tasks fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/analytics/dashboard/executive`, { headers, params }).catch((e) => { console.error('Executive fetch error:', e.message); return { data: null }; }),
        axios.get(`${API_URL}/api/ai-orchestration/dashboard`, { headers }).catch(() => ({ data: null }))
      ]);

      setReferralData(referrals.data);
      setMemberData(members.data);
      setCommunicationData(communications.data);
      setProgramData(programs.data);
      setTaskData(tasks.data);
      setExecutiveData(executive.data);
      setAiDashboardData(aiDashboard.data);

      // Fetch enhanced data (Phase 4)
      Promise.all([
        axios.get(`${API_URL}/api/analytics/referrals/outcomes`, { headers, params }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/communications/engagement`, { headers, params }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/members/enrichment-summary`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/analytics/insights`, { headers }).catch(() => ({ data: null })),
      ]).then(([outcomes, commEng, memberEnr, insightsRes]) => {
        setReferralOutcomes(outcomes.data);
        setCommEngagement(commEng.data);
        setMemberEnrichment(memberEnr.data);
        setInsights(insightsRes.data);
      });
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch saved reports
  const fetchSavedReports = async () => {
    setSavedReportsLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API_URL}/api/analytics/reports/saved`, {
        headers: _buildAxiosAuthHeaders(token)
      });
      setSavedReports(res.data.reports || []);
    } catch (error) {
      console.error('Error fetching saved reports:', error);
    } finally {
      setSavedReportsLoading(false);
    }
  };

  const handleCloneReport = async (reportId: number) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/analytics/reports/saved/${reportId}/clone`,
        {},
        { headers: _buildAxiosAuthHeaders(token) }
      );
      fetchSavedReports();
    } catch (error) {
      console.error('Error cloning report:', error);
      alert('Failed to clone report');
    }
  };

  const handleDeleteReport = async (reportId: number) => {
    if (!confirm('Are you sure you want to delete this report?')) return;
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${API_URL}/api/analytics/reports/saved/${reportId}`, {
        headers: _buildAxiosAuthHeaders(token)
      });
      setSavedReports(prev => prev.filter(r => r.id !== reportId));
    } catch (error) {
      console.error('Error deleting report:', error);
      alert('Failed to delete report');
    }
  };

  const handleRunSavedReport = async (reportId: number) => {
    setRunningReportId(reportId);
    setReportError(null);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${API_URL}/api/analytics/reports/saved/${reportId}/run`,
        {},
        { headers: _buildAxiosAuthHeaders(token) }
      );
      setReportResult(res.data);
      setReportError(null);
      // Scroll to results
      setTimeout(() => {
        document.querySelector('[data-report-results]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    } catch (error: any) {
      console.error('Error running saved report:', error);
      const detail = error?.response?.data?.detail || 'Failed to run saved report';
      setReportError(detail);
      alert(`Report failed: ${detail}`);
    } finally {
      setRunningReportId(null);
    }
  };

  // Toggle distribution panel for a saved report
  const handleToggleDistribute = async (reportId: number) => {
    if (distributeReportId === reportId) {
      setDistributeReportId(null);
      return;
    }
    setDistributeReportId(reportId);
    setDistDelivery('download');
    setDistSftpHost(''); setDistSftpPort('22'); setDistSftpUser(''); setDistSftpDir('');
    setDistRequiresApproval(false);
    setDistAffiliationId('');
    // Fetch affiliations if not loaded
    if (distAffiliations.length === 0) {
      try {
        const token = localStorage.getItem('auth_token');
        const cboId = localStorage.getItem('active_cbo_id') || '1';
        const res = await axios.get(`${API_URL}/api/organization-setup/${cboId}/affiliations`, {
          headers: _buildAxiosAuthHeaders(token)
        });
        const affs = Array.isArray(res.data) ? res.data : [];
        setDistAffiliations(affs);
        if (affs.length > 0) setDistAffiliationId(String(affs[0].id));
      } catch { setDistAffiliations([]); }
    }
  };

  const handleCreateDistribution = async (reportId: number, reportName: string) => {
    if (!distAffiliationId) { alert('Please select an MCO / Health Plan'); return; }
    setDistSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      await axios.post(
        `${API_URL}/api/organization-setup/affiliations/${distAffiliationId}/report-configs`,
        {
          report_name: `${reportName} — Distribution`,
          report_type: 'saved_report',
          included_fields: ['*'],
          delivery_method: distDelivery,
          sftp_host: distSftpHost || null,
          sftp_port: parseInt(distSftpPort),
          sftp_username: distSftpUser || null,
          sftp_remote_dir: distSftpDir || null,
          requires_approval: distRequiresApproval,
          saved_report_id: reportId,
        },
        { headers: _buildAxiosAuthHeaders(token) }
      );
      alert('Distribution config created! Go to Report Distribution tab to generate and deliver.');
      setDistributeReportId(null);
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to create distribution config');
    } finally {
      setDistSaving(false);
    }
  };

  // Fetch schema sources for advanced report builder
  const fetchSchemaSources = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API_URL}/api/analytics/schema/sources`, {
        headers: _buildAxiosAuthHeaders(token)
      });
      setSchemaSources(res.data.sources || []);
    } catch (error) {
      console.error('Error fetching schema sources:', error);
    }
  };

  // Fetch report templates
  const fetchReportTemplates = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${API_URL}/api/analytics/reports/templates`, {
        headers: _buildAxiosAuthHeaders(token)
      });
      setReportTemplates(res.data.templates || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchSavedReports();
    fetchSchemaSources();
    fetchReportTemplates();
  }, [dateRange]);

  // Export data as CSV
  const exportToCSV = (data: any, filename: string) => {
    const csvContent = convertToCSV(data);
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const convertToCSV = (data: any): string => {
    if (!data || typeof data !== 'object') return '';

    // If data has columns and rows (report result format), export as table
    if (data.columns && data.rows && Array.isArray(data.rows)) {
      const headers = data.columns.join(',');
      const rows = data.rows.map((row: any) =>
        data.columns.map((col: string) => {
          const val = row[col];
          if (val === null || val === undefined) return '';
          const str = String(val);
          return str.includes(',') || str.includes('"') || str.includes('\n')
            ? `"${str.replace(/"/g, '""')}"` : str;
        }).join(',')
      );
      return headers + '\n' + rows.join('\n');
    }

    // If data has a summary object, flatten it
    if (data.summary && typeof data.summary === 'object') {
      const headers = Object.keys(data.summary);
      const values = headers.map(k => {
        const v = data.summary[k];
        return v !== null && v !== undefined ? String(v) : '';
      });
      let csv = headers.join(',') + '\n' + values.join(',');

      // Add distributions as additional sections
      for (const [key, dist] of Object.entries(data)) {
        if (key !== 'summary' && dist && typeof dist === 'object' && !Array.isArray(dist)) {
          csv += '\n\n' + key.replace(/_/g, ' ').toUpperCase();
          csv += '\nCategory,Count';
          for (const [k, v] of Object.entries(dist as Record<string, any>)) {
            csv += `\n${k},${v}`;
          }
        }
      }
      return csv;
    }

    // Fallback: key-value pairs
    const rows = Object.entries(data).map(([key, value]) => {
      const val = typeof value === 'object' ? JSON.stringify(value) : String(value ?? '');
      return `"${key}","${val.replace(/"/g, '""')}"`;
    });
    return 'Key,Value\n' + rows.join('\n');
  };

  // Export report result data
  const exportReportResult = (format: string) => {
    if (!reportResult) return;
    const name = (reportResult.report_name || 'report').replace(/\s+/g, '_').toLowerCase();

    if (format === 'csv') {
      exportToCSV(reportResult, name);
    } else if (format === 'json') {
      const blob = new Blob([JSON.stringify(reportResult, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  // Keep URL hash in sync with the active report so reports are deep-linkable.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const desired = `#report=${activeTab}`;
    if (window.location.hash !== desired) {
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${desired}`);
    }
  }, [activeTab]);

  // React to hash changes from the browser (back/forward, manual edits, deep links).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onHash = () => {
      const id = _readHashReport();
      if (id) setActiveTab(id);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // The Disaster Case Management section is capability-gated rather than
  // org-id-gated: `dcm_dashboard` decides whether the org has it, exactly as
  // `recovery_plan` gates the member-profile Recovery Plan section. The
  // backend enforces the same capability, so hiding the entry is presentation
  // only — it is not the security boundary.
  const { enabled: dcmDashboardEnabled } = useCapability(organizationId, 'dcm_dashboard');

  // TRUS-567: each Report Library entry is a `reports.library.<id>` page
  // capability a super admin can switch off per org (Page Access Management).
  // filterReportCatalog fails open — while loading or on error nothing is hidden.
  const { results: reportAccess, loading: reportAccessLoading } = usePageCapabilities(
    organizationId,
    REPORT_LIBRARY_PAGE_KEYS,
  );

  // LCC only for now (TODO: productize via nav registry): expose the
  // members-by-program Enrollments report under the Members category.
  const reportCatalog = useMemo(() => {
    let catalog = DEFAULT_REPORT_CATALOG;

    if (!dcmDashboardEnabled) {
      catalog = catalog.filter((c) => c.id !== 'recovery');
    }

    if (Number(getActiveCboId()) === 350) {
      catalog = catalog.map((c) =>
        c.id === 'members'
          ? { ...c, items: [...c.items, { id: 'enrollments', label: 'Enrollments', icon: ClipboardList }] }
          : c,
      );
    }

    return filterReportCatalog(catalog, reportAccessLoading ? null : reportAccess);
  }, [dcmDashboardEnabled, reportAccess, reportAccessLoading]);

  // If the active report (e.g. from a #report= deep link) was switched off for
  // this org, land on the first report that is still visible.
  useEffect(() => {
    if (reportAccessLoading || !isReportHidden(reportAccess, activeTab)) return;
    const first = reportCatalog[0]?.items[0]?.id;
    if (first) setActiveTab(first as AnalyticsTab);
  }, [activeTab, reportAccess, reportAccessLoading, reportCatalog]);

  // Lookup the active report's human label from the catalog so the PDF export
  // header stays in sync with the selection.
  const activeReportLabel = useMemo(() => {
    for (const cat of reportCatalog) {
      const m = cat.items.find((i) => i.id === activeTab);
      if (m) return m.label;
    }
    return 'Report';
  }, [activeTab, reportCatalog]);

  // Page-level "Export full report as PDF" — snapshots the main scroll area.
  const exportFullReportPdf = async () => {
    if (!mainScrollRef.current) return;
    try {
      setPageExportBusy('pdf');
      const { toPng } = await import('html-to-image');
      const { jsPDF } = await import('jspdf');
      // Snapshot the scroll content (the full report, not just what's visible).
      const target = mainScrollRef.current.firstElementChild as HTMLElement | null;
      if (!target) return;
      const dataUrl = await toPng(target, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: '#ffffff',
        filter: (node) => !(node instanceof HTMLElement && node.dataset.exportIgnore === 'true'),
      });
      const rect = target.getBoundingClientRect();
      const pdf = new jsPDF({
        orientation: rect.width >= rect.height ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'a4',
      });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 32;
      const maxW = pageW - margin * 2;
      const maxH = pageH - margin * 2 - 50;
      const scale = Math.min(maxW / rect.width, maxH / rect.height);
      pdf.setFontSize(13);
      pdf.text(`${activeReportLabel} — Report`, margin, margin + 6);
      pdf.setFontSize(9);
      pdf.setTextColor(120);
      pdf.text(
        `${dateRange.startDate.toLocaleDateString()} – ${dateRange.endDate.toLocaleDateString()}  ·  ${new Date().toLocaleString()}`,
        margin,
        margin + 24,
      );
      pdf.setTextColor(0);
      const renderW = rect.width * scale;
      const renderH = rect.height * scale;
      pdf.addImage(dataUrl, 'PNG', margin + (maxW - renderW) / 2, margin + 50, renderW, renderH);
      const fname = `${activeTab}-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      pdf.save(fname);
    } catch (e) {
      console.error('Full report PDF export failed', e);
      alert('PDF export failed. See console for details.');
    } finally {
      setPageExportBusy(null);
    }
  };

  // Page-level Print — uses native print with a print stylesheet that hides chrome.
  const printFullReport = () => {
    try {
      setPageExportBusy('print');
      window.print();
    } finally {
      // Reset after the print dialog returns
      setTimeout(() => setPageExportBusy(null), 200);
    }
  };

  if (loading && !referralData) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto mb-3 h-8 w-8 animate-spin text-v2accent" />
          <p className="text-sm text-ink-500">{t('analytics.loading_analytics', 'Loading analytics...')}</p>
        </div>
      </div>
    );
  }

  const exportCurrentCsv = () => {
    const exportMap: Record<string, { data: any; name: string }> = {
      executive: { data: executiveData, name: 'executive_summary' },
      referrals: { data: referralData, name: 'referrals' },
      members: { data: memberData, name: 'members' },
      communications: { data: communicationData, name: 'communications' },
      programs: { data: programData, name: 'programs' },
      tasks: { data: taskData, name: 'tasks' },
    };
    const current = exportMap[activeTab];
    if (current?.data) exportToCSV(current.data, current.name);
  };

  const toIsoDate = (d: Date) => d.toISOString().slice(0, 10);
  const fromIsoDate = (s: string) => new Date(`${s}T00:00:00`);

  const pageActions = (
    <>
      <div
        className="flex items-center gap-1.5 rounded-md border border-v2border bg-paper px-2 py-1 text-xs text-ink-700"
        title="Date range for this report"
      >
        <Calendar className="h-3.5 w-3.5 text-ink-500" />
        <input
          type="date"
          value={toIsoDate(dateRange.startDate)}
          onChange={(e) =>
            setDateRange((r) => ({ ...r, startDate: fromIsoDate(e.target.value) }))
          }
          className="bg-transparent text-xs text-ink focus:outline-none"
          aria-label="Start date"
        />
        <span className="text-ink-500">–</span>
        <input
          type="date"
          value={toIsoDate(dateRange.endDate)}
          onChange={(e) =>
            setDateRange((r) => ({ ...r, endDate: fromIsoDate(e.target.value) }))
          }
          className="bg-transparent text-xs text-ink focus:outline-none"
          aria-label="End date"
        />
      </div>
      <button
        type="button"
        onClick={exportCurrentCsv}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-v2border bg-paper px-2.5 text-xs font-medium text-ink hover:bg-paper-2"
        title="Export raw data as CSV"
      >
        <Download className="h-3.5 w-3.5" />
        CSV
      </button>
      <button
        type="button"
        onClick={exportFullReportPdf}
        disabled={pageExportBusy !== null}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-v2border bg-paper px-2.5 text-xs font-medium text-ink hover:bg-paper-2 disabled:opacity-60"
        title="Export entire report as PDF"
      >
        {pageExportBusy === 'pdf' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}
        PDF
      </button>
      <button
        type="button"
        onClick={printFullReport}
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-v2border bg-paper px-2.5 text-xs font-medium text-ink hover:bg-paper-2"
        title="Print this report"
      >
        <Printer className="h-3.5 w-3.5" />
        Print
      </button>
    </>
  );

  return (
    <CapabilityGate
      organizationId={organizationId}
      capability="analytics"
      upgradeMessage="Analytics and Reporting is not enabled for your organization. Contact your administrator to enable this feature."
    >
      <div className="reports-v2-layout flex h-full overflow-hidden">
          <ReportLibraryPanel
            categories={reportCatalog}
            activeId={activeTab}
            onSelect={(id) => setActiveTab(id as AnalyticsTab)}
          />
          <main ref={mainScrollRef} className="flex-1 overflow-y-auto bg-paper print:overflow-visible">
            <div className="reports-v2-print-area mx-auto max-w-6xl p-4 sm:p-6">
              <div className="mb-3 flex flex-wrap items-center justify-end gap-2 print:hidden" data-export-ignore="true">
                {pageActions}
              </div>
              <ExportableCard
                title=""
                exportSlug={activeTab}
              >

        {/* Executive Dashboard */}
        {activeTab === 'executive' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            {executiveData && (
              <>
                <div className="bg-teal-50 rounded-xl p-3 md:p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <TrendingUp className="w-4 h-4 md:w-5 md:h-5 text-teal-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_referrals', 'Total Referrals')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-teal-600">
                    {executiveData.referrals?.total_referrals || 0}
                  </p>
                </div>

                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <CheckSquare className="w-4 h-4 md:w-5 md:h-5 text-green-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.success_rate', 'Success Rate')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-green-600">
                    {executiveData.referrals?.success_rate || 0}%
                  </p>
                </div>

                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <Users className="w-4 h-4 md:w-5 md:h-5 text-blue-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.active_members', 'Active Members')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-blue-600">
                    {executiveData.members?.active_members || 0}
                  </p>
                </div>

                <div className="bg-purple-50 rounded-xl p-3 md:p-4 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-purple-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.messages_sent', 'Messages Sent')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-purple-600">
                    {executiveData.communications?.total_messages || 0}
                  </p>
                </div>

                <div className="bg-indigo-50 rounded-xl p-3 md:p-4 border border-indigo-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <FileText className="w-4 h-4 md:w-5 md:h-5 text-indigo-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.active_programs', 'Active Programs')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-indigo-600">
                    {executiveData.programs?.active_programs || 0}
                  </p>
                </div>

                <div className="bg-orange-50 rounded-xl p-3 md:p-4 border border-orange-100 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className="w-4 h-4 md:w-5 md:h-5 text-orange-600" />
                    <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.tasks_completed', 'Tasks Completed')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-orange-600">
                    {executiveData.tasks?.completed_tasks || 0}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* AI Activity Widget */}
          {aiDashboardData && (
            <div className="bg-gradient-to-br from-purple-50 via-indigo-50 to-blue-50 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 rounded-xl shadow-md border-2 border-indigo-200 dark:border-gray-600 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <div className="flex items-center space-x-2 mb-4">
                <Brain className="w-5 h-5 md:w-6 md:h-6 text-indigo-600 dark:text-indigo-400" />
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100">{t('analytics.ai_orchestration_activity', 'AI Orchestration Activity')}</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border border-indigo-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    <Activity className="w-4 h-4 text-indigo-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('analytics.tasks_today', 'Tasks Today')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-indigo-600">
                    {aiDashboardData.total_tasks_today || 0}
                  </p>
                  <div className="flex items-center space-x-1 mt-1 text-xs text-gray-500 dark:text-gray-400">
                    <Zap className="w-3 h-3" />
                    <span>{aiDashboardData.running_tasks || 0} running</span>
                  </div>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border border-green-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    <CheckSquare className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('analytics.success_rate_1', 'Success Rate')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-green-600">
                    {aiDashboardData.avg_confidence_score ? (aiDashboardData.avg_confidence_score * 100).toFixed(0) : 0}%
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('analytics.avg_confidence', 'Avg confidence')}</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border border-amber-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    <DollarSign className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('analytics.cost_today', 'Cost Today')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-amber-600">
                    ${typeof aiDashboardData.total_cost_today === 'number' ? aiDashboardData.total_cost_today.toFixed(2) : '0.00'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{aiDashboardData.total_tokens_today?.toLocaleString() || 0} tokens</p>
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 border border-red-100 dark:border-gray-700 shadow-sm">
                  <div className="flex items-center space-x-2 mb-1">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{t('analytics.failed_tasks', 'Failed Tasks')}</span>
                  </div>
                  <p className="text-xl md:text-2xl font-bold text-red-600">
                    {aiDashboardData.failed_tasks || 0}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('analytics.needs_attention', 'Needs attention')}</p>
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-indigo-200 dark:border-gray-600">
                <p className="text-xs text-gray-600 dark:text-gray-400 flex items-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-teal-600" />
                  <span>
                    <strong>{aiDashboardData.top_category || 'Data Enrichment'}</strong> is the most active AI category today
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
            <h2 className="text-lg md:text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.key_performance_indicators', 'Key Performance Indicators')}</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{t('analytics.overview_of_critical_metrics_across', 'Overview of critical metrics across all domains')}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('analytics.referral_performance', 'Referral Performance')}</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between items-center py-1">
                    <span className="text-gray-600 dark:text-gray-400">{t('analytics.acceptance_rate', 'Acceptance Rate:')}</span>
                    <span className="font-semibold text-teal-600">{executiveData?.referrals?.acceptance_rate || 0}%</span>
                  </li>
                  <li className="flex justify-between items-center py-1">
                    <span className="text-gray-600 dark:text-gray-400">{t('analytics.avg_response_time', 'Avg Response Time:')}</span>
                    <span className="font-semibold text-teal-600">{executiveData?.referrals?.avg_response_time_hours || 0}h</span>
                  </li>
                </ul>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-white dark:from-gray-700 dark:to-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">{t('analytics.program_utilization', 'Program Utilization')}</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex justify-between items-center py-1">
                    <span className="text-gray-600 dark:text-gray-400">{t('analytics.utilization_rate', 'Utilization Rate:')}</span>
                    <span className="font-semibold text-indigo-600">{executiveData?.programs?.utilization_rate || 0}%</span>
                  </li>
                  <li className="flex justify-between items-center py-1">
                    <span className="text-gray-600 dark:text-gray-400">{t('analytics.active_enrollments', 'Active Enrollments:')}</span>
                    <span className="font-semibold text-indigo-600">{executiveData?.programs?.active_enrollments || 0}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Referrals Tab */}
        {activeTab === 'referrals' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {referralData && (
              <>
                <div className="bg-teal-50 rounded-xl p-3 md:p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_referrals_1', 'Total Referrals')}</span>
                  <p className="text-xl md:text-2xl font-bold text-teal-600 mt-2">
                    {referralData.summary?.total_referrals || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.success_rate_2', 'Success Rate')}</span>
                  <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">
                    {referralData.summary?.success_rate || 0}%
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.avg_response_time_1', 'Avg Response Time')}</span>
                  <p className="text-xl md:text-2xl font-bold text-blue-600 mt-2">
                    {referralData.summary?.avg_response_time_hours || 0}h
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.status_distribution', 'Status Distribution')}</h3>
              {referralData?.status_distribution && (
                <PieChartComponent
                  data={Object.entries(referralData.status_distribution).map(([name, value]) => ({
                    name,
                    value
                  }))}
                  dataKey="value"
                  nameKey="name"
                />
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.urgency_levels', 'Urgency Levels')}</h3>
              {referralData?.urgency_distribution && (
                <BarChartComponent
                  data={Object.entries(referralData.urgency_distribution).map(([name, value]) => ({
                    name,
                    count: value
                  }))}
                  dataKeys={['count']}
                  xKey="name"
                />
              )}
            </div>
          </div>

          {/* Enhanced Referral Data - Phase 4 */}
          {referralOutcomes && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Avg Satisfaction</span>
                  <p className="text-xl font-bold text-amber-600 mt-1">{referralOutcomes.quality?.avg_satisfaction || 0}/5</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 border border-red-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">SLA Compliance</span>
                  <p className="text-xl font-bold text-red-600 mt-1">{referralOutcomes.sla?.compliance_rate || 0}%</p>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Avg Cycle Time</span>
                  <p className="text-xl font-bold text-indigo-600 mt-1">{referralOutcomes.quality?.avg_cycle_days || 0}d</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Collection Rate</span>
                  <p className="text-xl font-bold text-emerald-600 mt-1">{referralOutcomes.billing?.collection_rate || 0}%</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {referralOutcomes.outcome_distribution && Object.keys(referralOutcomes.outcome_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Service Outcomes</h3>
                    <PieChartComponent
                      data={Object.entries(referralOutcomes.outcome_distribution).map(([name, value]) => ({ name, value }))}
                      dataKey="value"
                      nameKey="name"
                    />
                  </div>
                )}
                {referralOutcomes.source_distribution && Object.keys(referralOutcomes.source_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Referral Sources</h3>
                    <BarChartComponent
                      data={Object.entries(referralOutcomes.source_distribution).map(([name, value]) => ({ name: name.replace(/_/g, ' '), count: value }))}
                      dataKeys={['count']}
                      xKey="name"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        )}

        {/* Enrollments (members-by-program) report — LCC only, under Members */}
        {activeTab === 'enrollments' && <EnrollmentReportPanel />}

        {/* Disaster Case Management — caseload overview (TRUS-538) */}
        {activeTab === 'dcm-overview' && dcmDashboardEnabled && <DcmCaseOverviewPanel />}
        {activeTab === 'dcm-activity' && dcmDashboardEnabled && <DcmTeamActivityPanel />}

        {/* Members Tab */}
        {activeTab === 'members' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {memberData && (
              <>
                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_members', 'Total Members')}</span>
                  <p className="text-xl md:text-2xl font-bold text-blue-600 mt-2">
                    {memberData.summary?.total_members || 0}
                  </p>
                </div>
                <div className="bg-teal-50 rounded-xl p-3 md:p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.active_members_1', 'Active Members')}</span>
                  <p className="text-xl md:text-2xl font-bold text-teal-600 mt-2">
                    {memberData.summary?.active_members || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.data_quality_score', 'Data Quality Score')}</span>
                  <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">
                    {memberEnrichment?.avg_scores?.data_quality || memberData.summary?.avg_data_quality_score || 0}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.age_distribution', 'Age Distribution')}</h3>
              {memberData?.age_distribution && (
                <BarChartComponent
                  data={Object.entries(memberData.age_distribution).map(([name, value]) => ({
                    name,
                    count: value
                  }))}
                  dataKeys={['count']}
                  xKey="name"
                />
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.insurance_distribution', 'Insurance Distribution')}</h3>
              {memberData?.insurance_distribution && (
                <PieChartComponent
                  data={Object.entries(memberData.insurance_distribution).map(([name, value]) => ({
                    name,
                    value
                  }))}
                  dataKey="value"
                  nameKey="name"
                />
              )}
            </div>
          </div>

          {/* Enhanced Member Data - Phase 4 (Enrichment + Retention) */}
          {memberEnrichment && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-purple-50 rounded-xl p-3 border border-purple-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Data Quality</span>
                  <p className="text-xl font-bold text-purple-600 mt-1">{memberEnrichment.avg_scores?.data_quality || 0}</p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 border border-red-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">SDOH Risk</span>
                  <p className="text-xl font-bold text-red-600 mt-1">{memberEnrichment.avg_scores?.sdoh_risk || 0}</p>
                </div>
                <div className="bg-teal-50 rounded-xl p-3 border border-teal-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Engagement</span>
                  <p className="text-xl font-bold text-teal-600 mt-1">{memberEnrichment.avg_scores?.engagement || 0}</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">High SDOH Risk</span>
                  <p className="text-xl font-bold text-amber-600 mt-1">{memberEnrichment.high_sdoh_risk_count || 0}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-3 border border-emerald-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Well Enriched</span>
                  <p className="text-xl font-bold text-emerald-600 mt-1">{memberEnrichment.enrichment?.well_enriched || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                {memberEnrichment.sdoh_risk_distribution && Object.keys(memberEnrichment.sdoh_risk_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">SDOH Risk Distribution</h3>
                    <PieChartComponent
                      data={Object.entries(memberEnrichment.sdoh_risk_distribution).map(([name, value]) => ({ name, value }))}
                      dataKey="value"
                      nameKey="name"
                    />
                  </div>
                )}
                {memberEnrichment.retention_risk_distribution && Object.keys(memberEnrichment.retention_risk_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Retention Risk</h3>
                    <BarChartComponent
                      data={Object.entries(memberEnrichment.retention_risk_distribution).map(([name, value]) => ({ name, count: value }))}
                      dataKeys={['count']}
                      xKey="name"
                    />
                  </div>
                )}
                {memberEnrichment.enrollment_status_distribution && Object.keys(memberEnrichment.enrollment_status_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Enrollment Status</h3>
                    <PieChartComponent
                      data={Object.entries(memberEnrichment.enrollment_status_distribution).map(([name, value]) => ({ name, value }))}
                      dataKey="value"
                      nameKey="name"
                    />
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            {communicationData && (
              <>
                <div className="bg-purple-50 rounded-xl p-3 md:p-4 border border-purple-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_messages', 'Total Messages')}</span>
                  <p className="text-xl md:text-2xl font-bold text-purple-600 mt-2">
                    {communicationData.summary?.total_messages || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.delivery_rate', 'Delivery Rate')}</span>
                  <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">
                    {communicationData.summary?.delivery_rate || 0}%
                  </p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 md:p-4 border border-red-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.failed_messages', 'Failed Messages')}</span>
                  <p className="text-xl md:text-2xl font-bold text-red-600 mt-2">
                    {communicationData.summary?.failed_messages || 0}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.channel_distribution', 'Channel Distribution')}</h3>
            {communicationData?.channel_distribution && (
              <BarChartComponent
                data={Object.entries(communicationData.channel_distribution).map(([name, value]) => ({
                  name,
                  count: value
                }))}
                dataKeys={['count']}
                xKey="name"
              />
            )}
          </div>

          {/* Enhanced Communication Data - Phase 4 */}
          {commEngagement && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-blue-50 rounded-xl p-3 border border-blue-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Avg Response Time</span>
                  <p className="text-xl font-bold text-blue-600 mt-1">{commEngagement.response_metrics?.avg_hours_to_first_response || 0}h</p>
                </div>
                <div className="bg-amber-50 rounded-xl p-3 border border-amber-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Opt-Out Rate</span>
                  <p className="text-xl font-bold text-amber-600 mt-1">{commEngagement.opt_outs?.opt_out_rate || 0}%</p>
                </div>
                <div className="bg-teal-50 rounded-xl p-3 border border-teal-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Action Completion</span>
                  <p className="text-xl font-bold text-teal-600 mt-1">{commEngagement.actions?.completion_rate || 0}%</p>
                </div>
                <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100 shadow-sm">
                  <span className="text-xs font-medium text-gray-600">Avg Engagement</span>
                  <p className="text-xl font-bold text-indigo-600 mt-1">{commEngagement.response_metrics?.avg_engagement_score || 0}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {commEngagement.engagement_funnel && Object.keys(commEngagement.engagement_funnel).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Engagement Funnel</h3>
                    <BarChartComponent
                      data={Object.entries(commEngagement.engagement_funnel).map(([name, value]) => ({ name: name.replace(/_/g, ' '), count: value }))}
                      dataKeys={['count']}
                      xKey="name"
                    />
                  </div>
                )}
                {commEngagement.priority_distribution && Object.keys(commEngagement.priority_distribution).length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4">
                    <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">Message Priority</h3>
                    <PieChartComponent
                      data={Object.entries(commEngagement.priority_distribution).map(([name, value]) => ({ name, value }))}
                      dataKey="value"
                      nameKey="name"
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {/* Message History Report — integrated from Communication History */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-500" />
                Message History Report
              </h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={async () => {
                    try {
                      const token = localStorage.getItem('auth_token');
                      const res = await axios.post(`${API_URL}/api/analytics/reports/save`, {
                        name: `Communication Report — ${new Date().toLocaleDateString()}`,
                        description: 'Auto-generated communication history report',
                        config: {
                          dataSource: 'communications',
                          dateRange: { startDate: dateRange.startDate.toISOString(), endDate: dateRange.endDate.toISOString() },
                          metrics: ['total_messages', 'delivery_rate', 'failed_messages'],
                          dimensions: ['channel'],
                          visualizationType: 'table',
                        },
                      }, { headers: _buildAxiosAuthHeaders(token) });
                      alert('Report saved to Analytics → Saved Reports');
                      fetchSavedReports();
                    } catch (err) {
                      console.error('Failed to save report', err);
                    }
                  }}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 text-blue-600 border border-blue-200 rounded-lg hover:bg-blue-50"
                >
                  <Save className="w-3 h-3" /> Save as Report
                </button>
                <button
                  onClick={() => {
                    // Export current communication data as CSV
                    if (!communicationData) return;
                    const rows = [
                      ['Metric', 'Value'],
                      ['Total Messages', communicationData.summary?.total_messages || 0],
                      ['Delivery Rate', `${communicationData.summary?.delivery_rate || 0}%`],
                      ['Failed Messages', communicationData.summary?.failed_messages || 0],
                    ];
                    if (communicationData.channel_distribution) {
                      rows.push(['', '']);
                      rows.push(['Channel', 'Count']);
                      Object.entries(communicationData.channel_distribution).forEach(([ch, cnt]) => {
                        rows.push([ch, String(cnt)]);
                      });
                    }
                    const csv = rows.map(r => r.join(',')).join('\n');
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `communication-report-${new Date().toISOString().split('T')[0]}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="flex items-center gap-1.5 text-xs px-3 py-1.5 text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
                >
                  <Download className="w-3 h-3" /> Export CSV
                </button>
              </div>
            </div>
            <CommunicationHistoryPage embedded={true} />
          </div>
        </div>
        )}

        {/* Programs Tab */}
        {activeTab === 'programs' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {programData && (
              <>
                <div className="bg-indigo-50 rounded-xl p-3 md:p-4 border border-indigo-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_programs', 'Total Programs')}</span>
                  <p className="text-xl md:text-2xl font-bold text-indigo-600 mt-2">
                    {programData.summary?.total_programs || 0}
                  </p>
                </div>
                <div className="bg-teal-50 rounded-xl p-3 md:p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.active_programs_1', 'Active Programs')}</span>
                  <p className="text-xl md:text-2xl font-bold text-teal-600 mt-2">
                    {programData.summary?.active_programs || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.utilization_rate_1', 'Utilization Rate')}</span>
                  <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">
                    {programData.summary?.utilization_rate || 0}%
                  </p>
                </div>
                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.active_enrollments_1', 'Active Enrollments')}</span>
                  <p className="text-xl md:text-2xl font-bold text-blue-600 mt-2">
                    {programData.summary?.active_enrollments || 0}
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
            <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.program_types', 'Program Types')}</h3>
            {programData?.program_type_distribution && (
              <PieChartComponent
                data={Object.entries(programData.program_type_distribution).map(([name, value]) => ({
                  name,
                  value
                }))}
                dataKey="value"
                nameKey="name"
              />
            )}
          </div>
        </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-3 md:space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {taskData && (
              <>
                <div className="bg-blue-50 rounded-xl p-3 md:p-4 border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.total_tasks', 'Total Tasks')}</span>
                  <p className="text-xl md:text-2xl font-bold text-blue-600 mt-2">
                    {taskData.summary?.total_tasks || 0}
                  </p>
                </div>
                <div className="bg-green-50 rounded-xl p-3 md:p-4 border border-green-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.completed', 'Completed')}</span>
                  <p className="text-xl md:text-2xl font-bold text-green-600 mt-2">
                    {taskData.summary?.completed_tasks || 0}
                  </p>
                </div>
                <div className="bg-red-50 rounded-xl p-3 md:p-4 border border-red-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.overdue', 'Overdue')}</span>
                  <p className="text-xl md:text-2xl font-bold text-red-600 mt-2">
                    {taskData.summary?.overdue_tasks || 0}
                  </p>
                </div>
                <div className="bg-teal-50 rounded-xl p-3 md:p-4 border border-teal-100 shadow-sm hover:shadow-md transition-shadow">
                  <span className="text-xs md:text-sm font-medium text-gray-600 dark:text-gray-400">{t('analytics.completion_rate', 'Completion Rate')}</span>
                  <p className="text-xl md:text-2xl font-bold text-teal-600 mt-2">
                    {taskData.summary?.completion_rate || 0}%
                  </p>
                </div>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.task_status', 'Task Status')}</h3>
              {taskData?.status_distribution && (
                <PieChartComponent
                  data={Object.entries(taskData.status_distribution).map(([name, value]) => ({
                    name,
                    value
                  }))}
                  dataKey="value"
                  nameKey="name"
                />
              )}
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-4 md:p-6 hover:shadow-lg transition-shadow">
              <h3 className="text-base md:text-lg font-semibold text-gray-800 dark:text-gray-100 mb-4 pb-2 border-b border-gray-100 dark:border-gray-700">{t('analytics.task_priority', 'Task Priority')}</h3>
              {taskData?.priority_distribution && (
                <BarChartComponent
                  data={Object.entries(taskData.priority_distribution).map(([name, value]) => ({
                    name,
                    count: value
                  }))}
                  dataKeys={['count']}
                  xKey="name"
                />
              )}
            </div>
          </div>
        </div>
        )}

        {/* Custom Reports Tab */}
        {activeTab === 'custom' && (
          <div className="space-y-6">

          {/* Report Templates */}
          {reportTemplates.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                {t('analytics.report_templates', 'Report Templates')}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {reportTemplates.slice(0, 6).map((tpl: any) => (
                  <button
                    key={tpl.id}
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('auth_token');
                        setReportError(null);
                        setReportResult(null);
                        const response = await axios.post(
                          `${API_URL}/api/analytics/reports/build-advanced`,
                          {
                            ...tpl.config,
                            primary_source: tpl.config.primary_source,
                            report_name: tpl.name,
                            date_range: {
                              start_date: dateRange.startDate.toISOString(),
                              end_date: dateRange.endDate.toISOString(),
                            },
                          },
                          { headers: _buildAxiosAuthHeaders(token) }
                        );
                        setReportResult(response.data);
                      } catch (error: any) {
                        setReportError(error.response?.data?.detail || 'Failed to run template');
                      }
                    }}
                    className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-300 hover:bg-indigo-50 dark:hover:bg-gray-600 transition-all text-left"
                  >
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{tpl.name}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{tpl.description}</p>
                    <span className="inline-block mt-2 text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full capitalize">{tpl.category}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Schema Sources Info */}
          {schemaSources.length > 0 && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 rounded-xl border border-blue-200 dark:border-gray-600 p-4">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-semibold text-blue-900 dark:text-blue-200">
                  {t('analytics.advanced_report_builder', 'Advanced Report Builder')}
                </span>
              </div>
              <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
                {schemaSources.length} data sources available with {schemaSources.reduce((acc: number, s: any) => acc + s.metrics.length, 0)} metrics
                and {schemaSources.reduce((acc: number, s: any) => acc + s.dimensions.length, 0)} dimensions.
                Use the wizard below or select a template above.
              </p>
              <div className="flex flex-wrap gap-2">
                {schemaSources.map((s: any) => (
                  <span key={s.key} className="text-xs px-2 py-1 bg-white dark:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-full border border-blue-200 dark:border-gray-500">
                    {s.label} ({s.metrics.length})
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Error Display */}
          {reportError && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-red-900 dark:text-red-100 mb-1">{t('analytics.error_running_report', 'Error Running Report')}</h3>
                  <pre className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap font-sans">{reportError}</pre>
                </div>
                <button onClick={() => setReportError(null)} className="flex-shrink-0 text-red-400 hover:text-red-600">
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
              </div>
            </div>
          )}

          {/* Report Result Display */}
          {reportResult && (
            <div data-report-results className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-green-200 dark:border-green-800 p-4 md:p-6">
              {/* Header with title, view toggle, export buttons */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <CheckSquare className="w-5 h-5 text-green-600" />
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {reportResult.report_name || 'Report Results'}
                  </h3>
                  {reportResult.total_rows !== undefined && (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 rounded-full">
                      {reportResult.total_rows} rows
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {/* View toggle */}
                  {reportResult.columns && reportResult.rows && reportResult.rows.length > 0 && (
                    <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-0.5">
                      <button
                        onClick={() => setReportResult({ ...reportResult, _viewMode: 'table' })}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${(!reportResult._viewMode || reportResult._viewMode === 'table') ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}
                      >Table</button>
                      <button
                        onClick={() => setReportResult({ ...reportResult, _viewMode: 'bar' })}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${reportResult._viewMode === 'bar' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}
                      >Bar Chart</button>
                      <button
                        onClick={() => setReportResult({ ...reportResult, _viewMode: 'pie' })}
                        className={`px-3 py-1 text-xs rounded-md transition-all ${reportResult._viewMode === 'pie' ? 'bg-white dark:bg-gray-600 shadow-sm text-gray-900 dark:text-white' : 'text-gray-500'}`}
                      >Pie Chart</button>
                    </div>
                  )}

                  {/* Export buttons */}
                  <button
                    onClick={() => exportReportResult('csv')}
                    className="px-3 py-1.5 text-xs bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" /> CSV
                  </button>
                  <button
                    onClick={() => exportReportResult('json')}
                    className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-white rounded-lg hover:bg-gray-300 flex items-center gap-1"
                  >
                    <Download className="w-3 h-3" /> JSON
                  </button>

                  {/* Close */}
                  <button onClick={() => setReportResult(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                  </button>
                </div>
              </div>

              {/* Report content based on view mode */}
              {reportResult.columns && reportResult.rows && reportResult.rows.length > 0 ? (
                <>
                  {/* Table View (default) */}
                  {(!reportResult._viewMode || reportResult._viewMode === 'table') && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            {reportResult.columns.map((col: string) => (
                              <th key={col} className="px-3 py-2 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase">
                                {col.replace(/_/g, ' ')}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {reportResult.rows.slice(0, 100).map((row: any, idx: number) => (
                            <tr key={idx} className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                              {reportResult.columns.map((col: string) => (
                                <td key={col} className="px-3 py-2 text-gray-800 dark:text-gray-200">
                                  {row[col] !== null && row[col] !== undefined ? String(row[col]) : '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Bar Chart View */}
                  {reportResult._viewMode === 'bar' && (
                    <BarChartComponent
                      data={reportResult.rows.slice(0, 20).map((row: any) => {
                        const entry: any = {};
                        reportResult.columns.forEach((col: string) => {
                          entry[col.replace(/_/g, ' ')] = typeof row[col] === 'number' ? row[col] : row[col];
                        });
                        return entry;
                      })}
                      dataKeys={reportResult.columns.filter((c: string) => {
                        // Use numeric columns as data keys
                        const sample = reportResult.rows[0]?.[c];
                        return typeof sample === 'number';
                      }).map((c: string) => c.replace(/_/g, ' '))}
                      xKey={(() => {
                        // Use first non-numeric column as x-axis
                        const strCol = reportResult.columns.find((c: string) => typeof reportResult.rows[0]?.[c] !== 'number');
                        return strCol ? strCol.replace(/_/g, ' ') : reportResult.columns[0]?.replace(/_/g, ' ');
                      })()}
                    />
                  )}

                  {/* Pie Chart View */}
                  {reportResult._viewMode === 'pie' && (
                    <PieChartComponent
                      data={reportResult.rows.slice(0, 10).map((row: any) => {
                        const strCol = reportResult.columns.find((c: string) => typeof reportResult.rows[0]?.[c] !== 'number') || reportResult.columns[0];
                        const numCol = reportResult.columns.find((c: string) => typeof reportResult.rows[0]?.[c] === 'number') || reportResult.columns[1];
                        return {
                          name: row[strCol] ?? 'Unknown',
                          value: typeof row[numCol] === 'number' ? row[numCol] : 0,
                        };
                      })}
                      dataKey="value"
                      nameKey="name"
                    />
                  )}
                </>
              ) : reportResult.data ? (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-xs text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{JSON.stringify(reportResult.data, null, 2)}</pre>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-xs text-gray-800 dark:text-gray-100 whitespace-pre-wrap">{JSON.stringify(reportResult, null, 2)}</pre>
                </div>
              )}
            </div>
          )}

          <ReportBuilderWizard
            onRunReport={async (config) => {
              try {
                setReportError(null);
                setReportResult(null);
                const token = localStorage.getItem('auth_token');

                // Use the advanced builder endpoint which executes real queries
                const payload = {
                  primary_source: config.dataSource,
                  joined_sources: config.joinedSources || [],
                  metrics: config.metrics,
                  dimensions: config.dimensions,
                  granularity: config.granularity || null,
                  date_range: {
                    start_date: config.dateRange.startDate.toISOString(),
                    end_date: config.dateRange.endDate.toISOString(),
                  },
                  visualization_type: config.visualizationType,
                  filters: config.filters,
                  report_name: config.name,
                  export_format: config.exportFormat,
                };

                const response = await axios.post(
                  `${API_URL}/api/analytics/reports/build-advanced`,
                  payload,
                  { headers: _buildAxiosAuthHeaders(token) }
                );

                setReportResult(response.data);
              } catch (error: any) {
                console.error('Error running report:', error);
                let errorMessage = 'Failed to generate report. Please try again.';
                if (error.response?.data?.detail) {
                  errorMessage = typeof error.response.data.detail === 'string'
                    ? error.response.data.detail
                    : JSON.stringify(error.response.data.detail);
                } else if (error.response?.status === 401) {
                  errorMessage = 'Authentication failed. Please log in again.';
                } else if (error.message) {
                  errorMessage = error.message;
                }
                setReportError(errorMessage);
              }
            }}
            onSaveReport={async (config) => {
              try {
                const token = localStorage.getItem('auth_token');
                await axios.post(
                  `${API_URL}/api/analytics/reports/save`,
                  {
                    name: config.name,
                    description: config.description || '',
                    config: config
                  },
                  { headers: _buildAxiosAuthHeaders(token) }
                );
                alert('Report saved successfully!');
                fetchSavedReports();
              } catch (error) {
                console.error('Error saving report:', error);
                alert('Failed to save report');
              }
            }}
          />

          {/* Saved Reports Panel */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 md:p-6 mt-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-teal-600" />
                {t('analytics.saved_reports', 'Saved Reports')}
              </h3>
              <button
                onClick={fetchSavedReports}
                className="text-sm text-teal-600 hover:text-teal-700"
              >
                {t('analytics.refresh', 'Refresh')}
              </button>
            </div>

            {savedReportsLoading ? (
              <div className="text-center py-8 text-gray-500">{t('analytics.loading', 'Loading...')}</div>
            ) : savedReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>{t('analytics.no_saved_reports', 'No saved reports yet. Build and save a report above to see it here.')}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {savedReports.map((report) => (
                  <div
                    key={report.id}
                    className={`rounded-lg border transition-all ${
                      distributeReportId === report.id
                        ? 'border-blue-300 dark:border-blue-600'
                        : 'border-gray-200 dark:border-gray-600 hover:border-teal-300'
                    }`}
                  >
                  <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-t-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">{report.name}</h4>
                      <div className="flex items-center gap-3 mt-1">
                        <span className="text-xs px-2 py-0.5 bg-teal-100 text-teal-700 rounded-full capitalize">
                          {report.data_source}
                        </span>
                        {report.description && (
                          <span className="text-xs text-gray-500 truncate">{report.description}</span>
                        )}
                        <span className="text-xs text-gray-400">
                          {report.updated_at ? new Date(report.updated_at).toLocaleDateString() : ''}
                        </span>
                        {report.last_run_at && (
                          <span className="text-xs text-gray-400">
                            Last run: {new Date(report.last_run_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleRunSavedReport(report.id)}
                        disabled={runningReportId === report.id}
                        className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                          runningReportId === report.id
                            ? 'bg-teal-400 text-white cursor-wait'
                            : 'bg-teal-600 text-white hover:bg-teal-700'
                        }`}
                        title="Run report"
                      >
                        {runningReportId === report.id ? 'Running...' : t('analytics.run', 'Run')}
                      </button>
                      <button
                        onClick={() => handleCloneReport(report.id)}
                        className="p-1.5 text-gray-500 hover:text-teal-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                        title="Clone report"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      {report.is_owner && (
                        <button
                          onClick={() => handleDeleteReport(report.id)}
                          className="p-1.5 text-gray-500 hover:text-red-600 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                          title="Delete report"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleDistribute(report.id)}
                        className={`p-1.5 rounded-lg transition-all ${
                          distributeReportId === report.id
                            ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
                            : 'text-gray-500 hover:text-blue-600 hover:bg-gray-100 dark:hover:bg-gray-600'
                        }`}
                        title="Set up distribution (SFTP, email, approval)"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Distribution Setup Panel */}
                  {distributeReportId === report.id && (
                    <div className="px-4 pb-4 pt-2 bg-blue-50/50 dark:bg-blue-900/10 border-t border-blue-200 dark:border-blue-800 rounded-b-lg -mt-1 space-y-3">
                      <div className="flex items-center gap-2 text-sm font-medium text-blue-700 dark:text-blue-300">
                        <Send className="w-4 h-4" />
                        Set Up Distribution for "{report.name}"
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {/* MCO / Health Plan */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">MCO / Health Plan *</label>
                          <select
                            value={distAffiliationId}
                            onChange={(e) => setDistAffiliationId(e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
                          >
                            <option value="">Select...</option>
                            {distAffiliations.map((aff: any) => (
                              <option key={aff.id} value={String(aff.id)}>
                                {aff.payer_name || aff.health_plan_name || `Affiliation #${aff.id}`}
                                {aff.program_name ? ` — ${aff.program_name}` : ''}
                              </option>
                            ))}
                          </select>
                          {distAffiliations.length === 0 && (
                            <p className="text-[10px] text-amber-600 mt-0.5">No affiliations found. Set up program affiliations first.</p>
                          )}
                        </div>

                        {/* Delivery Method */}
                        <div>
                          <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Delivery Method</label>
                          <select
                            value={distDelivery}
                            onChange={(e) => setDistDelivery(e.target.value)}
                            className="w-full px-2 py-1.5 border rounded-md text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600"
                          >
                            <option value="download">Download</option>
                            <option value="email">Email</option>
                            <option value="sftp">SFTP</option>
                          </select>
                        </div>

                        {/* Approval */}
                        <div className="flex items-center gap-2 pt-4">
                          <input
                            type="checkbox"
                            id={`approval-${report.id}`}
                            checked={distRequiresApproval}
                            onChange={(e) => setDistRequiresApproval(e.target.checked)}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <label htmlFor={`approval-${report.id}`} className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
                            Requires supervisor approval
                          </label>
                        </div>
                      </div>

                      {/* SFTP Config */}
                      {distDelivery === 'sftp' && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-2.5 border border-blue-200 dark:border-blue-700 rounded-lg bg-blue-50/30 dark:bg-blue-900/20">
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-0.5">SFTP Host *</label>
                            <input value={distSftpHost} onChange={(e) => setDistSftpHost(e.target.value)} placeholder="sftp.mco.com"
                              className="w-full px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-0.5">Port</label>
                            <input value={distSftpPort} onChange={(e) => setDistSftpPort(e.target.value)} type="number"
                              className="w-full px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-0.5">Username</label>
                            <input value={distSftpUser} onChange={(e) => setDistSftpUser(e.target.value)} placeholder="meca_uploads"
                              className="w-full px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600" />
                          </div>
                          <div>
                            <label className="text-[10px] font-medium text-gray-500 block mb-0.5">Remote Dir</label>
                            <input value={distSftpDir} onChange={(e) => setDistSftpDir(e.target.value)} placeholder="/inbound/reports/"
                              className="w-full px-2 py-1 border rounded text-xs bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600" />
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleCreateDistribution(report.id, report.name)}
                          disabled={distSaving || !distAffiliationId}
                          className={`px-3 py-1.5 text-xs rounded-lg transition-all ${
                            distSaving || !distAffiliationId
                              ? 'bg-blue-300 text-white cursor-not-allowed'
                              : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          {distSaving ? 'Creating...' : 'Create Distribution Config'}
                        </button>
                        <button
                          onClick={() => setDistributeReportId(null)}
                          className="px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                        >
                          Cancel
                        </button>
                        <span className="text-[10px] text-gray-400 ml-2">
                          Config will appear in the Report Distribution tab for generation and delivery.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                ))}
              </div>
            )}
          </div>
        </div>
        )}

        {/* Member Enrichment Tab */}
        {activeTab === 'enrichment' && (
          <EnrichmentDashboard />
        )}

        {/* Consent Management Tab */}
        {activeTab === 'consent' && (
          <ConsentDashboard />
        )}

        {/* Incentives Tab */}
        {activeTab === 'incentives' && (
          <ProgramMilestoneDashboardSelector />
        )}

        {/* Provider Performance Tab */}
        {activeTab === 'provider-performance' && (
          <CapacityDashboard />
        )}

        {/* Telehealth Reports Tab */}
        {activeTab === 'telehealth' && (
          <TelehealthReporting />
        )}

        {/* Engagement Insights Tab — Phase 5c/6b CBO-scoped ML + analytics */}
        {activeTab === 'engagement-insights' && (
          <EngagementInsightsPanel />
        )}

        {/* Survey Results — live results of public-link surveys + CSV (TRUS-624) */}
        {activeTab === 'survey-results' && (
          <SurveyResultsReport />
        )}

        {/* ECM Insights Tab — Phase 6c live-schema SDOH / care plan / enrollments */}
        {activeTab === 'ecm-insights' && (
          <ECMInsightsPanel />
        )}

        {/* ECM Progress Notes — consolidated cross-member progress notes + CSV (TRUS-265) */}
        {activeTab === 'ecm-progress-notes' && (
          <EcmProgressNotesReport />
        )}

        {/* SFHP Invoice — the 54-column payer file. Deliberately SEPARATE from the
            progress-notes report above: that one is the clinical artifact, this one is
            what SFHP parses by column position. Conflating them is the bug this fixes. */}
        {activeTab === 'sfhp-invoice' && (
          <SfhpInvoiceReport />
        )}

        {/* Retention Intelligence — churn risk + ML predictions (member_retention) */}
        {activeTab === 'member-retention' && (
          <MemberRetentionDashboard cboId={organizationId ?? 1} />
        )}

        {/* Environmental Impact — per-federation disaster exposure × engagement gap */}
        {activeTab === 'environmental-impact' && (
          <EnvironmentalImpactCard />
        )}

        {/* Campaign Progress Tab — UUHP demo Flow 3 reporting surface */}
        {activeTab === 'campaign-progress' && (
          <div className="space-y-3">
            <UUHPCampaignProgressPage
              cboId={parseInt(localStorage.getItem('active_cbo_id') || '303', 10) || 303}
            />
          </div>
        )}

        {/* Report Distribution Tab */}
        {activeTab === 'report-distribution' && (
          <ReportDistributionPage />
        )}

        {/* Scheduled Reports. The Report Library's onSelect sets THIS component's
            activeTab, not the app router's — so a catalog entry without a render
            block here shows an empty panel and never calls its API. The page is
            also an app.tsx route for the command palette; both render the same
            component. */}
        {activeTab === 'scheduled-reports' && (
          <ScheduledReportsPage />
        )}
              </ExportableCard>
            </div>
          </main>
      </div>
    </CapabilityGate>
  );
};
